import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { ConfigService } from '../../services/config.service';
import { MapService } from '../../map/services/map.service';
import { MetadataService } from '../../services/metadata.service';
import { SearchService } from '../../search/services/search.service';
import { ViewerService } from '../../services/viewer.service';
import { DocViewerFactoryService } from '../../services/factory/doc-viewer-factory.service';
import { CopyLinkComponent } from '../../components/copy-link/copy-link.component';
import { formatString } from '../../utils/string-utils';
import dayjs from 'dayjs';

import { loadModules } from 'esri-loader';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser


export interface Record {
	id: string | number;
	title: string;
	type: string;
	imgURL: string;
	geo: any;
	metadata: { [key: string]: any };
	hasPages: boolean;
	pages: number;
	hasProjected: boolean;
	projectedPages: any[];
	hasFeatures: boolean;
	arcGISQueryURL: string;
}

/** 
 * Component displaying a preview of a mappable geo record in a floating panel.
 */
@Component({
	selector: 'map-record-preview',
	templateUrl: './map-record-preview.component.html',
	styleUrl: './map-record-preview.component.css'
})
export class MapRecordPreviewComponent implements OnInit, OnDestroy {

	@Input() mapView: any;

	private mapEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;
	private userEventSubscription!: Subscription;

	Object = Object;
	LOCALIZATION: any;
	dateFormatDisplay: string = 'MM/DD/YY';

	arcgis: { [key: string]: any } = {}; // Loaded in displayFeatures()
	geohash: any;

	minimized: boolean = true;
	projectedRecords: any = {};
	currentProjectionPage: number = 0;

	record: Record = {
		id: '',
		title: '',
		type: '',
		imgURL: '',
		geo: {},
		metadata: {},
		hasPages: false,
		pages: 0,
		hasProjected: false,
		projectedPages: [],
		hasFeatures: false,
		arcGISQueryURL: ''
	};

	records: any[] = [];
	page: number = 1;
	perpage: number = 1;
	hasPrev: boolean = false;
	hasNext: boolean = false;
	pagingLabel: string = '';
	total: number = 0;

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private configService: ConfigService,
		private mapService: MapService,
		private metadataService: MetadataService,
		private viewerService: ViewerService,
		private searchService: SearchService,
		private docViewerFactory: DocViewerFactoryService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "USER_INTERFACE_DATE_TYPE" });
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';

		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if ('localizationLoaded' === event) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe((event: any) => {
			if ('searchEnded' === event.type) {
				this.projectedRecords = {};
			}
			else if ('clearSearch' === event.type) {
				this.projectedRecords = {};
			}
		});

		this.mapEventSubscription = this.mapService.mapServiceEvent.subscribe((event: any) => {
			if ('updateMapRecordPreview' === event.eventType) {
				this.createPreview(event.data);
			}
		});
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}

		if (this.mapEventSubscription) {
			this.mapEventSubscription.unsubscribe();
		}
	}

	// Main initializer - called in response to event listener
	private async createPreview(data: any): Promise<void> {
		let uuid = data.uuid;
		let geohash = data.geoHash;
		this.page = 1;
		this.records = [];
		this.minimized = false;

		if (this.arcgis['featureLayer']) {
			this.mapView.map.remove(this.arcgis['featureLayer']);
		}

		if (data.record) {
			this.processSingularRecord(data.record);
			return;
		}

		if (uuid) {
			let response = await this.searchService.getRecord(uuid);
			let info = response["csw:GetRecordByIdResponse"].iStoreRecord;
			let productInfo = this.processMetadata(info);

			this.record = {
				id: info.id,
				type: info.format,
				title: info.title,
				imgURL: formatString(this.configService.getConfig().record.thumbnailURL, info.id),
				geo: info.geo,
				metadata: productInfo,
				hasPages: false,
				pages: 0,
				hasProjected: false,
				projectedPages: [],
				hasFeatures: false,
				arcGISQueryURL: ''
			};

			let geo = info.geo;
			if (geo) {
				const [
					Point,
					Polyline,
					Multipoint,
					Polygon,
					SpatialReference,
					geometryService
				] = await loadModules([
					"esri/geometry/Point",
					"esri/geometry/Polyline",
					"esri/geometry/Multipoint",
					"esri/geometry/Polygon",
					"esri/geometry/SpatialReference",
					"esri/rest/geometryService"
				]);

				let p = null;

				// This function is defined in controllers.js
				let mpgeo = this.mapService.parseMultipointGeometry(geo);
				let primitive = Terraformer.WKT.parse(mpgeo);
				let newGeom = Terraformer.ArcGIS.convert(primitive);

				if (primitive instanceof Terraformer.LineString || primitive instanceof Terraformer.MultiLineString) {
					p = new Polyline(newGeom.paths);
				} else if (primitive instanceof Terraformer.Point) {
					p = new Point(newGeom);
				} else if (primitive instanceof Terraformer.MultiPoint) {
					try {
						p = new Multipoint(new SpatialReference({ wkid: 4326 }));
						p.points = newGeom.points;
					} catch (err) {
						// skip
					}
				} else if (primitive instanceof Terraformer.Polygon || primitive instanceof Terraformer.MultiPolygon) {
					p = new Polygon(newGeom.rings);
				} else {
					// fallback on Point
					p = new Point(newGeom.rings);
				}

				geometryService.simplify(this.configService.getConfig().esri.geometryService, [p]).then(
					(geometries: any) => {

						this.mapService.broadcastSelectedGeomChangeEvent({
							geometry: geometries[0],
							record: this.record.id,
							forceZoom: data.forceZoom
						});
					},
					(err: any) => {
						console.error("Error simplifying geometry: ", err);
					}
				);
			}

			this.processSingularRecord(this.record);

		} else if (geohash) {
			// Processes the cluster view
			this.geohash = geohash;
			this.loadGeoHashRecords();
		} else {
			//					$rootScope.$broadcast("unselectRecordMap"); // TODO
		}
	}

	private processSingularRecord(record: Record): void {
		this.viewerService.getDocumentInfo(record.id).then(
			(data: any) => {
				record.pages = data.pages;
				record.projectedPages = data.projectedPages;
				if (record.projectedPages && record.projectedPages.length > 0) {
					record.hasProjected = true;
				}
				if (data.arcGISQueryURL) {
					// For kmls, dwg and other file types that can be displayed on the map
					record.hasFeatures = true;
					record.arcGISQueryURL = data.arcGISQueryURL;
				}
				if (record.pages > 0 && record.type != 'PDF') {
					// PDF is a special case
					record.hasPages = true;
				}
				this.records.push(record);

				this.pagingLabel = "";
				this.hasNext = false;
				this.hasPrev = false;
				this.total = 1;
			},
			(err: any) => {
				console.error("Failed to get document info: ", err);
			}
		);
	}

	prevProjectionPage(): void {
		if (this.currentProjectionPage <= 0) {
			return;
		}
		this.currentProjectionPage--;
	}

	nextProjectionPage(): void {
		if (this.currentProjectionPage >= this.record.projectedPages.length - 1) {
			return;
		}
		this.currentProjectionPage++;
	}

	async getMapLayer(id: string): Promise<any> {
		const [GraphicsLayer, MediaLayer] = await loadModules(['esri/layers/GraphicsLayer', 'esri/layers/MediaLayer']);

		if (!this.mapView) {
			Promise.reject("Map is not ready.");
		}

		let layer = this.mapView.map.findLayerById(id);
		if (layer) return layer;

		switch (id) {
			case "projection":
				layer = new MediaLayer({
					id: "projection",
					source: [],
					opacity: 0.9,
					blendMode: "normal"
				});
				break;
		}

		if (layer) {
			this.mapView.map.add(layer);
			console.log("Created new layer with id '%s'", layer.id);
		}

		return layer;
	}

	async toggleProjection(id: string | number, page: number = 0): Promise<void> {
		if ((id || id == 0) && (page || page == 0)) {
			if (this.projectedRecords[id] && this.projectedRecords[id][page]) {
				let layer = await this.getMapLayer("projection");
				let isToggled = this.projectedRecords[id][page].toggled;
				if (isToggled) {
					layer.source.elements.add(this.projectedRecords[id][page].image);
				} else {
					layer.source.elements.remove(this.projectedRecords[id][page].image);
				}
				
				this.mapView.goTo(this.mapView.center, { animate: false }); // Jiggle the map to initiate a rerender
			}
		}
	}

	toggleOpacity(id: string | number, page: number): void {
		if ((id || id == 0) && (page || page == 0)) {
			if (this.projectedRecords[id] && this.projectedRecords[id][page]) {
				this.projectedRecords[id][page].image.opacity = this.projectedRecords[id][page].opacity;
			}
		}
	}

	centerFeatureSet() {
		if (this.arcgis['featureLayer']) {
			this.mapView.extent = this.arcgis['featureLayer'].fullExtent;
		}
	}

	async projectToMap(): Promise<void> {
		if (!this.record) { return; }

		const [
			ImageElement,
			ExtentAndRotationGeoreference,
			Extent
		] = await loadModules([
			"esri/layers/support/ImageElement",
			"esri/layers/support/ExtentAndRotationGeoreference",
			"esri/geometry/Extent"
		]);

		this.currentProjectionPage = 0;
		let layer = await this.getMapLayer("projection");

		for (const page of this.record.projectedPages) {
			const response = await this.mapService.getProjectedInfo(this.record.id, page);
			if (!response) {
				console.error("Error retrieving projected information.");
				return;
			}

			if (this.currentProjectionPage == 0) {
				this.currentProjectionPage = page;
			}

			let projectedJSON = this.mapService.getJSONfromXML(response);
			let url = projectedJSON.overlay_images.overlay.url;
			let bounds = projectedJSON.overlay_images.overlay.bounds;
			let extent = new Extent({
				xmin: bounds.west,
				ymin: bounds.south,
				xmax: bounds.east,
				ymax: bounds.north,
				spatialReference: { wkid: 4326 }
			});

			layer.spatialReference = extent.spatialReference;

			// The safest maximum image size is 2048 x 2048 pixels.
			let imageElement = new ImageElement({
				image: url,
				georeference: new ExtentAndRotationGeoreference({
					extent: extent
				})
			});

			if (!this.projectedRecords[this.record.id]) {
				this.projectedRecords[this.record.id] = {
					[this.currentProjectionPage]: {
						image: imageElement,
						toggled: true,
						opacity: 1
					}
				};

				layer.source.elements.add(imageElement);
			}

			if (!this.projectedRecords[this.record.id][page]) {
				layer.source.elements.add(imageElement);

				this.projectedRecords[this.record.id][page] = {
					image: imageElement,
					toggled: true,
					opacity: 1
				};
			}

			this.mapView.goTo(extent).catch((error: any) => {
				console.error("Error setting extent: ", error);
			});
		}
	}

	private loadFeatures(url: string, data: any): void {
		if (this.arcgis['featureLayer']) {
			this.mapView.map.remove(this.arcgis['featureLayer']);
		}

		this.mapService.loadFeatureMapLayer(url, data, {}).then(
			(layer: any) => {
				this.arcgis['featureLayer'] = layer;
				this.mapView.map.add(this.arcgis['featureLayer']);
				this.arcgis['featureLayer'].when(() => {
					this.mapView.goTo(this.arcgis['featureLayer'].fullExtent.expand(1.25))
						.catch((error: any) => {
							console.error("Error setting extent: ", error);
						});
				}).catch((error: any) => {
					console.error("Error loading GeoJSONLayer: ", error);
				})
			},
			(err: any) => {
				console.error("Failed to load feature map layer: ", err);
			}
		);
	}

	async displayFeatures(): Promise<void> {
		if (!this.record) { return; }

		let mainRecord = null;
		for (let i = 0; i < this.records.length; i++) {
			let record = this.records[i];
			if (record.id == this.record.id) {
				mainRecord = record;
				break;
			}
		}

		let countURL = mainRecord.arcGISQueryURL + "?returnCountOnly=" + true;
		let response = await this.mapService.getFeaturesCount(countURL);
		if (response.properties && response.properties.count && response.properties.count >= 0) {
			this.arcgis = {
				type: "geojson",
				pagination: {
					total: response.properties.count,
					page: 1,
					num_per_page: 1000,
					pagesize: [25, 50, 100, 1000]
				}
			};

			let offset = (this.arcgis['pagination'].num_per_page * (this.arcgis['pagination'].page - 1));
			let url = mainRecord.arcGISQueryURL;
			url += `?resultRecordCount=${this.arcgis['pagination'].num_per_page}`;
			url += `&resultOffset=${offset}`;

			this.mapService.getFeatures(url).then(
				(data: any) => {
					this.loadFeatures(url, data);
				},
				(err: any) => {
					console.error("Failed to load features: ", err);
				}
			);

		} else {
			this.arcgis = {
				type: "geojson"
			};
			this.loadFeatures(countURL, response);
		}
	}

	expandRecord(): void {
		if (!this.record) { return; }

		let mainRecord = null;
		for (let i = 0; i < this.records.length; i++) {
			let record = this.records[i];
			if (record.id == this.record.id) {
				mainRecord = record;
				break;
			}
		}
		this.mapService.broadcastExpandRecordEvent({ record: mainRecord });
	}

	async zoomToGeo(): Promise<void> {
		if (this.record && this.record.geo) {
			const [
				Point,
				Polyline,
				Multipoint,
				Polygon,
				SpatialReference,
				geometryService
			] = await loadModules([
				"esri/geometry/Point",
				"esri/geometry/Polyline",
				"esri/geometry/Multipoint",
				"esri/geometry/Polygon",
				"esri/geometry/SpatialReference",
				"esri/rest/geometryService"
			]);

			// Incoming geo is a WKT string
			let mpgeo = this.mapService.parseMultipointGeometry(this.record.geo);
			// Parse the WKT to a geometry. WKT points are in geographic coordinates
			let primitive = Terraformer.WKT.parse(mpgeo);
			let newGeom = Terraformer.ArcGIS.convert(primitive);
			let p = null;
			if (primitive instanceof Terraformer.LineString || primitive instanceof Terraformer.MultiLineString) {
				p = new Polyline(newGeom.paths);
			} else if (primitive instanceof Terraformer.Point) {
				p = new Point(newGeom);
			} else if (primitive instanceof Terraformer.MultiPoint) {
				try {
					p = new Multipoint(new SpatialReference({ wkid: 4326 }));
					p.points = newGeom.points;
				} catch (err) {
					// skip
				}
			} else if (primitive instanceof Terraformer.Polygon || primitive instanceof Terraformer.MultiPolygon) {
				p = new Polygon(newGeom.rings);
			} else {
				// fallback on Point
				p = new Point(newGeom.rings);
			}

			geometryService.simplify(this.configService.getConfig().esri.geometryService, [p]).then(
				(geometries: any) => {
					this.mapService.broadcastSelectedGeomChangeEvent({
						geometry: geometries[0],
						record: this.record.id,
						forceZoom: true
					});
				},
				(err: any) => {
					console.error("Error simplifying geometry: ", err);
				}
			);
		}
	}

	nextPage(): void {
		if (this.hasNext) {
			this.page++;
			this.loadGeoHashRecords();
		}
	}

	prevPage(): void {
		if (this.hasPrev) {
			this.page--;
			this.loadGeoHashRecords();
		}
	}

	private async loadGeoHashRecords(): Promise<void> {
		this.records = [];

		let response = await this.searchService.searchWithGeoHash(this.geohash, this.page, this.perpage);

		this.total = response.total ?? 0;
		let currRecord = response.records[0];
		if (!currRecord) {
			return;
		}

		let productInfo = this.processMetadata(currRecord);

		this.record = {
			id: currRecord.id,
			title: currRecord.title,
			type: currRecord.type,
			imgURL: formatString(this.configService.getConfig().record.thumbnailURL, currRecord.id),
			geo: currRecord.geo,
			metadata: productInfo,
			hasPages: false,
			pages: 0,
			hasProjected: false,
			projectedPages: [],
			hasFeatures: false,
			arcGISQueryURL: ''
		};

		if (currRecord.pages > 0) {
			this.record.hasPages = true;
		}
		this.records.push(this.record);

		let maxPage = Math.ceil(this.total / this.perpage);
		this.hasNext = this.page < maxPage;
		this.hasPrev = this.page > 1;

		this.pagingLabel = formatString("({0} of {1})", this.page, this.total);
	}

	private processMetadata(result: any, highlights?: any): any {
		let hl: { [key: string]: any } = {};
		if (highlights && highlights["aw:record"]["print"]) {
			hl = highlights["aw:record"]["print"];
		}

		let productHash = result;
		let productInfo: { [key: string]: any } = {};
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!productHash[field.name]) {
				continue;
			}

			productInfo[field.displayName] = {
				label: field.displayName,
				value: hl[field.name] ? hl[field.name] : productHash[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};

			productInfo[field.displayName].cleanedValue = this.metadataService.cleanValue(productInfo[field.displayName].value);
			productInfo[field.displayName].isValidCollection =
				this.metadataService.isValidFieldCollectionValue(productInfo[field.displayName].name, productInfo[field.displayName].cleanedValue);

			if (field.type == "DATE") {
				productInfo[field.displayName].value = dayjs(productInfo[field.displayName].value).format(this.dateFormatDisplay);
			}

			if (field.name == "requires") {
				productInfo[field.displayName].warning = true;
			}

			if (field.isLink) {
				productInfo[field.displayName].links = this.metadataService.linksFromString(productHash[field.name], field.name);
			} else {
				if (productInfo[field.displayName].value) {
					productInfo[field.displayName].value = productInfo[field.displayName].value.replace(/; /g, ';').replace(/;/g, '; ');
				}
			}
		}

		return productInfo;
	}

	/**
	 * Displays the product in the document viewer.
	 */
	openRecord(id: number | string): void {
		const viewerComponent = this.docViewerFactory.getViewerComponent();
		const modalRef = this.ngbModal.open(viewerComponent, {
			windowClass: "viewer-modal-window"
		});
		
		modalRef.componentInstance.productId = id;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	close(): void {
		if (this.arcgis['featureLayer']) {
			this.mapView.map.remove(this.arcgis['featureLayer']);
		}
		this.mapService.broadcastUnselectRecordEvent();
	}
}
